"use client";

import { useEffect, useRef, useState } from "react";

interface UseInfiniteScrollOptions {
  /**
   * Function to call when the sentinel comes into view
   */
  onLoadMore: () => void | Promise<void>;
  /**
   * Whether more items are available to load
   */
  hasMore: boolean;
  /**
   * Whether data is currently loading
   */
  isLoading?: boolean;
  /**
   * IntersectionObserver options
   */
  root?: Element | Document | null;
  rootMargin?: string;
  threshold?: number | number[];
}

/**
 * Hook to handle infinite scrolling using IntersectionObserver
 */
export function useInfiniteScroll({
  onLoadMore,
  hasMore,
  isLoading = false,
  root = null,
  rootMargin = "0px",
  threshold = 1.0,
}: UseInfiniteScrollOptions) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;

    if (!sentinel || isLoading || !hasMore) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        setIsIntersecting(entry.isIntersecting);

        if (entry.isIntersecting) {
          onLoadMore();
        }
      },
      {
        root,
        rootMargin,
        threshold,
      },
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [onLoadMore, hasMore, isLoading, root, rootMargin, threshold]);

  return { sentinelRef, isIntersecting };
}
